import { useState } from 'react'
import { Button, Col, Form, Input, InputNumber, Row, Select, Switch, Tag, Space, App, Tooltip } from 'antd'
import { PlusOutlined, EditOutlined, EnvironmentOutlined } from '@ant-design/icons'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { storesApi } from '@/features/gate/api'
import { plantsApi } from '@/features/masters/plants/api'
import type { Store } from '@/shared/types/master'
import DataTable from '@/shared/components/DataTable'
import FormDrawer from '@/shared/components/FormDrawer'
import PageHeader from '@/shared/components/PageHeader'
import { usePermission } from '@/shared/hooks/usePermission'

const STORE_TYPES = ['GODOWN', 'MIXING_FLOOR', 'SPARE_STORE', 'CONSUMABLE_STORE',
  'PACKING_STORE', 'QUARANTINE', 'OTHER']

const TYPE_LABELS: Record<string, string> = {
  GODOWN: '🏭 Godown / Warehouse',
  MIXING_FLOOR: '🔄 Mixing Floor',
  SPARE_STORE: '🔧 Spare Parts Store',
  CONSUMABLE_STORE: '📦 Consumable Store',
  PACKING_STORE: '📫 Packing Store',
  QUARANTINE: '🚫 Quarantine',
  OTHER: 'Other',
}

const typeColor: Record<string, string> = {
  GODOWN: 'blue', MIXING_FLOOR: 'orange', SPARE_STORE: 'cyan',
  CONSUMABLE_STORE: 'geekblue', PACKING_STORE: 'purple', QUARANTINE: 'red', OTHER: 'default',
}

export default function StoresPage() {
  const { message } = App.useApp()
  const qc = useQueryClient()
  const [form] = Form.useForm()
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(50)
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [editing, setEditing] = useState<Store | null>(null)
  const [filterType, setFilterType] = useState<string | undefined>()

  const canCreate = usePermission('store.create')
  const canUpdate = usePermission('store.update')

  const { data: plantsData } = useQuery({
    queryKey: ['plants-all'],
    queryFn: () => plantsApi.list({ page: 1, page_size: 200 }),
  })
  const plantOptions = plantsData?.items?.map((p: { id: string; name: string }) => ({ value: p.id, label: p.name })) ?? []

  const { data, isLoading } = useQuery({
    queryKey: ['stores', page, pageSize, filterType],
    queryFn: () => storesApi.list({ page, size: pageSize, ...(filterType ? { store_type: filterType } : {}) }),
  })

  const openCreate = () => {
    setEditing(null)
    form.resetFields()
    form.setFieldsValue({ is_active: true, store_type: 'GODOWN' })
    setDrawerOpen(true)
  }

  const openEdit = (record: Store) => {
    setEditing(record)
    form.setFieldsValue(record)
    setDrawerOpen(true)
  }

  const saveMutation = useMutation({
    mutationFn: (values: Record<string, unknown>) =>
      editing ? storesApi.update(editing.id, values) : storesApi.create(values as never),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['stores'] })
      message.success(editing ? 'Warehouse/Store updated' : 'Warehouse/Store created')
      setDrawerOpen(false)
    },
    onError: (e: Error) => message.error((e as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? 'Save failed'),
  })

  const onSubmit = () => form.validateFields().then((v) => saveMutation.mutate(v))

  const columns = [
    { title: 'Code', dataIndex: 'store_code', key: 'code', width: 100,
      render: (v: string) => <code style={{ fontSize: 11, background: '#f0f0f0', padding: '1px 5px', borderRadius: 3 }}>{v}</code> },
    { title: 'Name', dataIndex: 'store_name', key: 'name',
      render: (v: string, r: Store) => (
        <Space direction="vertical" size={0}>
          <span style={{ fontWeight: 600 }}>{v}</span>
          {r.location_description && (
            <span style={{ fontSize: 11, color: '#888' }}>
              <EnvironmentOutlined style={{ marginRight: 3 }} />{r.location_description}
            </span>
          )}
        </Space>
      )
    },
    {
      title: 'Type',
      dataIndex: 'store_type',
      key: 'type',
      width: 160,
      render: (v: string) => <Tag color={typeColor[v] ?? 'default'} style={{ fontSize: 11 }}>{TYPE_LABELS[v] ?? v}</Tag>,
    },
    { title: 'Plant', dataIndex: 'plant_name', key: 'plant', width: 120 },
    {
      title: 'Capacity',
      key: 'capacity',
      width: 130,
      render: (_: unknown, r: Store) => (
        <Space direction="vertical" size={0}>
          {r.capacity_bales != null && (
            <span style={{ fontSize: 12, color: '#1677ff', fontWeight: 600 }}>
              🧺 {r.capacity_bales.toLocaleString()} bales
            </span>
          )}
          {r.capacity_mt != null && (
            <span style={{ fontSize: 11, color: '#888' }}>{r.capacity_mt} MT</span>
          )}
          {r.capacity_bales == null && r.capacity_mt == null && <span style={{ color: '#bbb' }}>—</span>}
        </Space>
      ),
    },
    {
      title: 'Keeper',
      key: 'keeper',
      width: 130,
      render: (_: unknown, r: Store) => r.keeper_name
        ? (
          <Space direction="vertical" size={0}>
            <span style={{ fontSize: 12 }}>{r.keeper_name}</span>
            {r.keeper_phone && <span style={{ fontSize: 11, color: '#888' }}>{r.keeper_phone}</span>}
          </Space>
        )
        : <span style={{ color: '#bbb' }}>—</span>,
    },
    {
      title: 'Status', dataIndex: 'is_active', key: 'status', width: 80,
      render: (v: boolean) => <Tag color={v ? 'green' : 'red'}>{v ? 'Active' : 'Inactive'}</Tag>,
    },
    {
      title: '', key: 'actions', width: 80,
      render: (_: unknown, record: Store) =>
        canUpdate
          ? <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(record)}>Edit</Button>
          : null,
    },
  ]

  return (
    <div>
      <PageHeader
        title="Stores & Warehouses"
        subtitle="Manage godowns, warehouses, and storage locations"
        extra={
          <Space>
            <Select
              allowClear
              placeholder="Filter by type"
              style={{ width: 180 }}
              value={filterType}
              onChange={setFilterType}
              options={STORE_TYPES.map(t => ({ value: t, label: TYPE_LABELS[t] }))}
            />
            {canCreate && (
              <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>New Location</Button>
            )}
          </Space>
        }
      />
      <DataTable
        rowKey="id"
        columns={columns}
        dataSource={data?.items}
        loading={isLoading}
        total={data?.total}
        page={page}
        pageSize={pageSize}
        onPageChange={(p, ps) => { setPage(p); setPageSize(ps) }}
      />

      <FormDrawer
        title={editing ? 'Edit Warehouse / Store' : 'New Warehouse / Store'}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onSubmit={onSubmit}
        loading={saveMutation.isPending}
        form={form}
      >
        <Form.Item name="store_name" label="Warehouse / Store Name" rules={[{ required: true }]}>
          <Input placeholder="e.g. Main Godown A, Raw Material Store 1" />
        </Form.Item>
        <Form.Item name="store_type" label="Type" rules={[{ required: true }]}>
          <Select options={STORE_TYPES.map(t => ({ value: t, label: TYPE_LABELS[t] }))} />
        </Form.Item>
        <Form.Item name="plant_id" label="Plant" rules={[{ required: true }]}>
          <Select
            options={plantOptions}
            showSearch
            filterOption={(input, opt) => (opt?.label as string)?.toLowerCase().includes(input.toLowerCase())}
          />
        </Form.Item>
        <Form.Item name="location_description" label="Location / Address">
          <Input.TextArea rows={2} placeholder="e.g. North side of factory, near gate 2, Row A–D" />
        </Form.Item>
        <Row gutter={12}>
          <Col span={12}>
            <Form.Item name="capacity_bales" label={
              <Tooltip title="Maximum number of cotton bales this location can hold">
                Capacity (Bales) <span style={{ color: '#888', fontSize: 11 }}>ℹ</span>
              </Tooltip>
            }>
              <InputNumber style={{ width: '100%' }} min={0} step={100} placeholder="e.g. 500" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="capacity_mt" label="Capacity (MT)">
              <InputNumber style={{ width: '100%' }} min={0} step={0.5} placeholder="e.g. 50" />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={12}>
          <Col span={12}>
            <Form.Item name="keeper_name" label="Keeper / In-Charge Name">
              <Input placeholder="Full name" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="keeper_phone" label="Keeper Phone">
              <Input placeholder="Mobile number" />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item name="is_active" label="Active" valuePropName="checked">
          <Switch />
        </Form.Item>
      </FormDrawer>
    </div>
  )
}
